// SEO for a product page, built from the product itself so every product
// uploaded in the admin panel gets a good title, description and breadcrumb
// with no extra work. The admin's metaTitle / metaDescription still win
// where written. Used by the live page (src/Presets/Preset-item.js) and the
// server page (api/_lib/productShell.js, through the generated
// api/_lib/productSeo.cjs), so Google and shoppers see the same words. The
// admin panel has a byte-for-byte copy (admin/src/Data/productSeo.js) for its
// SEO preview; run `node scripts/check-taxonomy-sync.js` after editing.
// No imports: scripts/check-taxonomy-sync.js --write builds the CommonJS copy.
//
// Keywords come from Google Trends (India); see docs/seo-keyword-plan.md.

// The search each kind of product is found by, keyed "department/style".
export const PRODUCT_KEYWORDS = {
  "suit/suit": "Suit for Men",
  "suit/blazer": "Blazer for Men",
  "suit/jodhpuri": "Jodhpuri Suit for Men",
  "suit/tuxedo": "Tuxedo for Men",
  "suit/waistcoat": "Waistcoat for Men",
  "suit/safari-suit": "Safari Suit for Men",
  "kurta/kurta-pajama": "Kurta Pajama for Men",
  "kurta/kurta": "Kurta for Men",
  "kurta/nehru-jacket": "Nehru Jacket for Men",
  "kurta/kurta-jacket-set": "Kurta Jacket Set for Men",
  "kurta/sherwani": "Sherwani for Men",
  "kurta/pathani": "Pathani Suit for Men",
  "kurta/indo-western": "Indo Western for Men",
  "kurta/pajama": "Pajama for Men",
  "kurta/jubba": "Jubba for Men",
  "formal/shirt": "Shirt for Men",
  "formal/trouser": "Trousers for Men",
  "accessory/safa": "Safa for Groom",
  "accessory/dupatta": "Dupatta for Men",
  "accessory/mala": "Groom Mala",
  "accessory/islamic-cap": "Islamic Cap for Men",
  "accessory/mojari": "Mojari for Men",
  "accessory/pocket-square": "Pocket Square for Men",
  "accessory/brooch": "Brooch for Men",
  "accessory/tie": "Tie for Men",
  "accessory/cufflinks": "Cufflinks for Men",
  "accessory/caps-hats": "Cap for Men",
  "accessory/gamchha": "Cotton Gamchha",
  "accessory/shemagh": "Shemagh for Men",
  "accessory/gift-sets": "Gift Set for Men",
};

// The noun a product name must contain for "Buy <name> for Men" to read well.
const HEAD_NOUNS = {
  "Trousers for Men": ["trouser", "pant"],
  "Pajama for Men": ["pajama", "churidar", "dhoti", "patiala"],
  "Nehru Jacket for Men": ["jacket", "sadri", "koti", "bundi"],
  "Jodhpuri Suit for Men": ["jodhpuri", "bandhgala"],
  "Safa for Groom": ["safa", "pagdi", "turban"],
  "Islamic Cap for Men": ["cap", "topi", "kufi"],
  "Cap for Men": ["cap", "hat"],
  "Kurta Jacket Set for Men": ["set", "jacket", "sadri"],
};

const SITE_NAME = "Maviinci";

// "POWDER BLUE MEN SUIT" and "blue line" read as "Powder Blue Men Suit", "Blue Line".
export function displayName(name) {
  const n = String(name || "").replace(/\s+/g, " ").trim();
  if (!n) return "";
  if (n !== n.toUpperCase() && n !== n.toLowerCase()) return n;
  return n.toLowerCase().replace(/(^|[\s(/-])([a-z])/g, (m, a, b) => a + b.toUpperCase());
}

// Cut at the last full sentence that fits, else at a word, never mid-word.
export function smartCut(text, max) {
  const t = String(text || "").replace(/\s+/g, " ").trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max);
  const stop = Math.max(cut.lastIndexOf(". "), cut.lastIndexOf("! "), cut.lastIndexOf("? "));
  if (stop >= Math.min(90, max - 20)) return cut.slice(0, stop + 1);
  const space = cut.lastIndexOf(" ");
  return `${cut.slice(0, space > 40 ? space : max - 1).replace(/[,;:\s-]+$/, "")}…`;
}

/**
 * doc: the product (name, des, colorName, material, ...).
 * home: taxonomy homeOf(doc) — { department, style } or null.
 * Returns { name, keyword, title, description, crumbs }; crumbs are
 * [{ name, path }] from Home to the product (the product has no path).
 */
export function productSeo(doc, home) {
  const d = doc || {};
  const name = displayName(d.name) || "Maviinci look";
  // An accessory without its type still belongs to the accessories page.
  const dept = (home && home.department) || (d.productKind === "accessory" ? { slug: "accessory", label: "Accessories" } : null);
  const style = home && home.style;
  const fromCategory = d.category && !home && d.productKind !== "accessory"
    ? `${displayName(String(d.category).replace(/_/g, " ").toLowerCase())} for Men`
    : null;
  const b2b = Boolean(dept && dept.b2b);
  const keyword =
    (dept && style && PRODUCT_KEYWORDS[`${dept.slug}/${style.slug}`]) ||
    (b2b ? `${style ? style.label : "Staff"} Uniform` : style ? `${style.label} for Men` : dept && dept.slug === "accessory" ? "Men's Accessories" : dept ? `${dept.label} for Men` : fromCategory || "Menswear");

  // Title: "Buy Navy Blue Suit for Men Online" when the name already says what
  // it is, else "Diamond Cut – Kurta Pajama for Men"; Maviinci at the end
  // when it fits in about 60 characters.
  const lower = name.toLowerCase();
  const head = keyword.replace(/ for (Men|Groom)$/i, "").toLowerCase();
  const nouns = HEAD_NOUNS[keyword] || [head.split(" ").pop().replace(/s$/, "")];
  const saysWhat = nouns.some((w) => lower.includes(w));
  const forWho = /\bmen\b|\bgroom\b/i.test(name) ? "" : keyword.match(/ for (Men|Groom)$/i) ? ` for ${keyword.match(/ for (Men|Groom)$/i)[1]}` : "";
  const core = saysWhat ? `Buy ${name}${forWho} Online` : `${name} – ${keyword}`;
  const withSite = `${core} | ${SITE_NAME}`;
  const title = withSite.length <= 62 ? withSite : core.length <= 62 ? core : `${smartCut(name, 50)} | ${SITE_NAME}`;

  // Description: the admin's description when it says enough (cut cleanly),
  // else written from what the product is.
  const ready = dept && dept.slug === "accessory" && (!style || !style.sizing || style.sizing === "none");
  const fit = b2b
    ? "with your logo, stitched to each person's size and delivered across India"
    : ready
    ? "ready to ship across India"
    : style && (style.sizing === "head" || style.sizing === "foot")
      ? "made to your size and delivered across India"
      : "stitched to your measurements and delivered across India";
  const des = String(d.des || "").replace(/\s+/g, " ").trim();
  const what = [d.colorName, d.material].filter(Boolean).join(", ").toLowerCase();
  const description =
    des.length >= 80
      ? smartCut(des, 155)
      : smartCut(`Buy ${name} online: ${keyword.toLowerCase()}${what ? ` in ${what}` : ""}, ${fit}, with free shipping.`, 155);

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Studio Collection", path: "/products" },
    ...(dept ? [{ name: dept.label, path: `/products/${dept.slug}` }] : []),
    ...(dept && style && style.page ? [{ name: style.label, path: `/products/${dept.slug}/${style.slug}` }] : []),
    { name, path: null },
  ];
  return { name, keyword, title, description, crumbs };
}

// Every garment page answers how the measurements are taken (the AI engine:
// two photos and a height give seventeen tailor measurements). Accessories
// and uniforms have their own sizing, so they skip it.
export const MEASURE_FAQ = {
  q: "How do you take my measurements online?",
  a: "Give your height and weight and take a front and a side photo on your phone: our AI, trained on customers our own tailors measured by hand, gives all seventeen measurements a tailor needs, each with how sure it is. You can also pick your usual size, send a garment that fits, or measure with our video guide. Your face is blurred on your phone before upload, and if the fit is still off, alterations are free.",
};
export function needsMeasureFaq(doc) {
  const d = doc || {};
  return d.productKind !== "accessory" && d.productKind !== "uniform" && d.measurementProfile !== "none" && !d.accessoryType;
}
// The product's own questions, then the measurement one for garments.
export function productFaqs(doc) {
  const own = Array.isArray(doc && doc.faqs) ? doc.faqs.filter((f) => f && f.q && f.a) : [];
  return needsMeasureFaq(doc) && !own.some((f) => f.q === MEASURE_FAQ.q) ? [...own, MEASURE_FAQ] : own;
}
